import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContextService } from '@bcef/ia-shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged } from 'rxjs';

import { ChannelType, ClientType } from '@features/termination/enums';
import { PersonalInformation } from '@features/termination/interfaces';
import { Customer } from '@features/termination/models';
import { ConnectionContextService } from '@features/termination/services';
import { EMAIL_REGEX } from '@shared/utils';

import { PersonalInformationForm } from '../../forms';
import * as fromStore from '../../store';

@UntilDestroy()
@Injectable()
export class PersonalInformationFacade {
  private readonly contextService = inject(ContextService);

  private readonly connectionContextService: ConnectionContextService =
    inject(ConnectionContextService);

  private readonly store: Store = inject(Store);

  public personalInformationForm = new FormGroup<PersonalInformationForm>({
    email: new FormControl('', {
      validators: [Validators.pattern(EMAIL_REGEX)],
      nonNullable: true,
    }),
    dateOfBirth: new FormControl({ value: '', disabled: true }, { nonNullable: true }),
    fullName: new FormControl(
      { value: '', disabled: true },
      { validators: Validators.required, nonNullable: true },
    ),
    firstName: new FormControl(
      { value: '', disabled: true },
      { validators: Validators.required, nonNullable: true },
    ),
    gender: new FormControl(
      { value: 0, disabled: true },
      { validators: [Validators.required, Validators.min(1)], nonNullable: true },
    ),
    identification: new FormControl({ value: '', disabled: true }, { nonNullable: true }),
    applicantLastName: new FormControl('', { validators: Validators.required, nonNullable: true }),
    applicantFirstName: new FormControl('', { validators: Validators.required, nonNullable: true }),
    countryOfBirth: new FormControl('', { nonNullable: true }),
    communeOfBirth: new FormControl('', { nonNullable: true }),
    channelType: new FormControl('', { validators: Validators.required, nonNullable: true }),
    address: new FormControl('', { nonNullable: true }),
    postalCode: new FormControl('', { nonNullable: true }),
    city: new FormControl('', { nonNullable: true }),
  });

  public get email(): FormControl<string | null> {
    return this.personalInformationForm.controls.email;
  }

  public get dateOfBirth(): FormControl<string | null> {
    return this.personalInformationForm.controls.dateOfBirth;
  }

  public get fullName(): FormControl<string | null> {
    return this.personalInformationForm.controls.fullName;
  }

  public get firstName(): FormControl<string | null> {
    return this.personalInformationForm.controls.firstName;
  }

  public get gender(): FormControl<number | null> {
    return this.personalInformationForm.controls.gender;
  }

  public get firstNameValue(): string | null {
    return this.personalInformationForm.controls.firstName.value;
  }

  public get identification(): FormControl<string | null> {
    return this.personalInformationForm.controls.identification;
  }

  public get applicantLastName(): FormControl<string | null> {
    return this.personalInformationForm.controls.applicantLastName;
  }

  public get applicantFirstName(): FormControl<string | null> {
    return this.personalInformationForm.controls.applicantFirstName;
  }

  public get countryOfBirth(): FormControl<string | null> {
    return this.personalInformationForm.controls.countryOfBirth;
  }

  public get communeOfBirth(): FormControl<string | null> {
    return this.personalInformationForm.controls.communeOfBirth;
  }

  public get channelType(): FormControl<string | null> {
    return this.personalInformationForm.controls.channelType;
  }

  public get address(): FormControl<string | null> {
    return this.personalInformationForm.controls.address;
  }

  public get postalCode(): FormControl<string | null> {
    return this.personalInformationForm.controls.postalCode;
  }

  public get city(): FormControl<string | null> {
    return this.personalInformationForm.controls.city;
  }

  public isConnected = this.connectionContextService.isConnected;

  public isMobile = this.contextService.isMobile;

  constructor() {
    this.enablePersonalInformationFormForNotConnectedJourney();
    this.registerClientTypeFieldValidators();
    this.registerChannelTypeFieldValidators();
  }

  private enablePersonalInformationFormForNotConnectedJourney(): void {
    if (!this.isConnected) {
      this.personalInformationForm.enable();
    }
  }

  private registerClientTypeFieldValidators(): void {
    if (this.isConnected) {
      this.registerPhysicalPersonFieldValidatorsForConnectedJourney();
    } else {
      this.registerClientTypeFieldValidatorsForNotConnectedJourney();
    }
  }

  private registerPhysicalPersonFieldValidatorsForConnectedJourney(): void {
    this.store
      .select(fromStore.selectCustomerIsPhysicalPerson)
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value) {
          this.applicantLastName.removeValidators(Validators.required);
          this.applicantFirstName.removeValidators(Validators.required);
          this.countryOfBirth.addValidators(Validators.required);
          this.communeOfBirth.addValidators(Validators.required);

          this.personalInformationForm.updateValueAndValidity();
        }
      });
  }

  private registerClientTypeFieldValidatorsForNotConnectedJourney(): void {
    this.fullName.addValidators(Validators.required);
    this.dateOfBirth.addValidators(Validators.required);
    this.countryOfBirth.addValidators(Validators.required);
    this.communeOfBirth.addValidators(Validators.required);

    this.applicantLastName.removeValidators(Validators.required);
    this.applicantFirstName.removeValidators(Validators.required);

    this.identification.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        if (value === ClientType.PHYSICAL_PERSON) {
          this.gender.addValidators([Validators.required, Validators.min(1)]);
          this.firstName.addValidators(Validators.required);

          this.applicantLastName.removeValidators(Validators.required);
          this.applicantFirstName.removeValidators(Validators.required);

          this.applicantLastName.reset();
          this.applicantFirstName.reset();
        }

        if (value === ClientType.LEGAL_PERSON) {
          this.applicantLastName.addValidators(Validators.required);
          this.applicantFirstName.addValidators(Validators.required);

          this.gender.clearValidators();
          this.firstName.removeValidators(Validators.required);

          this.gender.reset();
          this.firstName.reset();
        }

        this.fullName.reset();
        this.dateOfBirth.reset();
        this.countryOfBirth.reset();
        this.communeOfBirth.reset();

        this.gender.markAsUntouched();
        this.fullName.markAsUntouched();
        this.firstName.markAsUntouched();
        this.applicantLastName.markAsUntouched();
        this.applicantFirstName.markAsUntouched();
        this.dateOfBirth.markAsUntouched();
        this.countryOfBirth.markAsUntouched();
        this.communeOfBirth.markAsUntouched();

        this.personalInformationForm.updateValueAndValidity();
      });
  }

  private registerChannelTypeFieldValidators(): void {
    this.channelType.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value === ChannelType.EMAIL) {
        this.email.addValidators(Validators.required);
        this.address.removeValidators(Validators.required);
        this.postalCode.removeValidators(Validators.required);
        this.city.removeValidators(Validators.required);

        this.email.markAsUntouched();
        this.address.reset();
        this.postalCode.reset();
        this.city.reset();
      }

      if (value === ChannelType.PAPER) {
        this.email.removeValidators(Validators.required);
        this.address.addValidators(Validators.required);
        this.postalCode.addValidators(Validators.required);
        this.city.addValidators(Validators.required);

        this.email.reset();
        this.address.markAsUntouched();
        this.postalCode.markAsUntouched();
        this.city.markAsUntouched();
      }

      this.personalInformationForm.updateValueAndValidity();
    });
  }

  public setPersonalInformationFormDefaultValues(): void {
    combineLatest([
      this.store.select(fromStore.selectCustomer).pipe(untilDestroyed(this)),
      this.store.select(fromStore.selectPersonalInformation).pipe(untilDestroyed(this)),
    ]).subscribe(([customer, personalInformation]) => {
      this.personalInformationForm.reset(
        this.buildPersonalInformation(personalInformation, customer || undefined),
      );
    });
  }

  private buildPersonalInformation(
    personalInformation: PersonalInformation,
    customer?: Customer,
  ): PersonalInformation {
    return {
      email: personalInformation.email,
      fullName: customer?.fullName || personalInformation.fullName,
      firstName: customer?.firstName || personalInformation.firstName,
      gender: customer?.genderWithMissCivilityFiltered || personalInformation.gender,
      dateOfBirth:
        customer?.dateOfBirth?.toLocaleDateString('fr-FR') || personalInformation.dateOfBirth,
      identification: customer?.identification || personalInformation.identification,
      applicantLastName: personalInformation.applicantLastName,
      applicantFirstName: personalInformation.applicantFirstName,
      countryOfBirth: personalInformation.countryOfBirth,
      communeOfBirth: personalInformation.communeOfBirth,
      channelType: personalInformation.channelType,
      address: personalInformation.address,
      postalCode: personalInformation.postalCode,
      city: personalInformation.city,
    };
  }

  public updatePersonalInformation(personalInformation: PersonalInformation): void {
    this.store.dispatch(fromStore.UpdatePersonalInformation({ personalInformation }));
  }
}
